/**
 * Date Utility Functions for IST (Indian Standard Time)
 *
 * Backend stores all dates in UTC
 * Frontend displays all dates in IST (Asia/Kolkata, UTC+5:30)
 *
 * Usage:
 * - Use formatDate/formatDateTime to display UTC dates in IST
 * - Use convertToUTC functions when sending dates to backend
 */

const IST_TIMEZONE = 'Asia/Kolkata';
const IST_LOCALE = 'en-IN';

/**
 * Format UTC date to IST date string
 * @param {string|Date} utcDate - UTC date from backend
 * @returns {string} - Formatted date in IST (e.g., "29/10/2025")
 */
export const formatDate = (utcDate) => {
  if (!utcDate) return 'N/A';
  const date = new Date(utcDate);
  return date.toLocaleDateString(IST_LOCALE, {
    timeZone: IST_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format UTC date to IST date and time string
 * @param {string|Date} utcDate - UTC date from backend
 * @returns {string} - Formatted date and time in IST (e.g., "29/10/2025, 1:35:24 AM")
 */
export const formatDateTime = (utcDate) => {
  if (!utcDate) return 'N/A';
  const date = new Date(utcDate);
  return date.toLocaleString(IST_LOCALE, {
    timeZone: IST_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Format UTC date to IST time string only
 * @param {string|Date} utcDate - UTC date from backend
 * @returns {string} - Formatted time in IST (e.g., "1:35:24 AM")
 */
export const formatTime = (utcDate) => {
  if (!utcDate) return 'N/A';
  const date = new Date(utcDate);
  return date.toLocaleTimeString(IST_LOCALE, {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Format UTC date to YYYY-MM-DD format in IST (for date inputs)
 * @param {string|Date} utcDate - UTC date from backend
 * @returns {string} - Date in YYYY-MM-DD format (IST)
 */
export const formatDateOnly = (utcDate) => {
  if (!utcDate) return '';
  const date = new Date(utcDate);

  // Get IST date parts using Intl.DateTimeFormat
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;

  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in IST as YYYY-MM-DD
 * @returns {string} - Today's date in IST (YYYY-MM-DD)
 */
export const getTodayIST = () => {
  const now = new Date();

  // Get IST date parts using Intl.DateTimeFormat
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const parts = formatter.formatToParts(now);
  const year = parts.find(p => p.type === 'year').value;
  const month = parts.find(p => p.type === 'month').value;
  const day = parts.find(p => p.type === 'day').value;

  return `${year}-${month}-${day}`;
};

/**
 * Convert IST date (YYYY-MM-DD) to UTC start of day
 * Used for "from_date" filters
 * @param {string} istDateString - Date in YYYY-MM-DD format (considered as IST)
 * @returns {string} - UTC timestamp for start of day
 */
export const getStartOfDayUTC = (istDateString) => {
  if (!istDateString) return '';

  // Create date at start of day in IST
  const [year, month, day] = istDateString.split('-');
  const istDate = new Date(`${year}-${month}-${day}T00:00:00+05:30`);

  return istDate.toISOString().split('.')[0] + 'Z';
};

/**
 * Convert IST date (YYYY-MM-DD) to UTC end of day
 * Used for "to_date" filters
 * @param {string} istDateString - Date in YYYY-MM-DD format (considered as IST)
 * @returns {string} - UTC timestamp for end of day
 */
export const getEndOfDayUTC = (istDateString) => {
  if (!istDateString) return '';

  // Create date at end of day in IST
  const [year, month, day] = istDateString.split('-');
  const istDate = new Date(`${year}-${month}-${day}T23:59:59+05:30`);

  return istDate.toISOString().split('.')[0] + 'Z';
};

/**
 * Convert IST datetime to UTC for backend
 * @param {string|Date} istDate - Date in IST
 * @returns {string} - UTC ISO string
 */
export const convertToUTC = (istDate) => {
  if (!istDate) return '';
  const date = new Date(istDate);
  return date.toISOString();
};

/**
 * Format date for CSV exports (includes IST label)
 * @param {string|Date} utcDate - UTC date from backend
 * @returns {string} - Formatted date with IST label
 */
export const formatDateForExport = (utcDate) => {
  if (!utcDate) return '';
  return formatDateTime(utcDate) + ' IST';
};
